/**
 * I18nLoader - Ensures translations are loaded before rendering children
 *
 * This component waits for i18next to be initialized and for the required
 * namespaces to be loaded before rendering the application.
 */

import { ReactNode, useEffect, useState } from 'react';
import i18n from '@/lib/i18n';
import { NAMESPACES } from '@/lib/i18n';

interface I18nLoaderProps {
  children: ReactNode;
}

export function I18nLoader({ children }: I18nLoaderProps) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadTranslations = async () => {
      try {
        // Wait for i18next to be initialized
        if (!i18n.isInitialized) {
          await new Promise<void>((resolve) => {
            i18n.on('initialized', () => resolve());
          });
        }

        // Load all namespaces for the current language
        await i18n.loadNamespaces([...NAMESPACES]);

        if (mounted) {
          setIsReady(true);
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
        // Still render the app even if translations fail
        if (mounted) {
          setIsReady(true);
        }
      }
    };

    loadTranslations();

    return () => {
      mounted = false;
    };
  }, []);

  // Listen for language changes and reload namespaces
  useEffect(() => {
    const handleLanguageChanged = () => {
      i18n.loadNamespaces([...NAMESPACES]);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-600"></div>
      </div>
    );
  }

  return <>{children}</>;
}
